import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
// The same calendar the client screen's empty appointment tab wears. One mark
// for both, because they are the same thing missing — nothing booked.
import { ReactComponent as NoAppointmentsMark } from "../../../assets/svg/No data images/appointment.svg";
import PageHeader from "../../../components/layout/PageHeader";
import { useHiddenTabBar } from "../../../components/layout/tabBarVisibility";
import {
  Button,
  DatePicker,
  EmptyState,
  FloatingAction,
  PlusIcon,
  SkeletonCard,
  failedEmptyState,
  formatDisplayDate,
  parseDisplayDate,
  useIsMobile,
  useLoadPhase,
  useToast,
} from "../../../components/ui";
import type { EmptyStateProps } from "../../../components/ui";
import { ApiError } from "../../../lib/http";
import {
  lockedProps,
  useSubscriptionGate,
} from "../../billing/hooks/useSubscriptionGate";
// Deep imports rather than the appointments barrel. Three of these five
// dialogs are not on it — they are opened from inside that feature and nowhere
// else — and the barrel pulls the whole client Appointment tab in behind
// whatever is taken from it. Same reasoning the rail uses for its two counts.
import CancelAppointmentModal from "../../appointments/components/CancelAppointmentModal";
import CompleteAppointmentModal from "../../appointments/components/CompleteAppointmentModal";
import MeetingModal from "../../appointments/components/MeetingModal";
import RescheduleAppointmentModal from "../../appointments/components/RescheduleAppointmentModal";
import ScheduleAppointmentModal from "../../appointments/components/ScheduleAppointmentModal";
import {
  appointmentShortDate,
  appointmentTime,
  attendeeName,
  dayFull,
} from "../../appointments/appointments";
// Which app makes the meeting links, and what to say about an online
// appointment when none does any more. Deep imports for the same reason the
// dialogs above are.
import { useMeetingApps } from "../../appointments/hooks/useMeetingApps";
import {
  MEETING_NOTICE_MS,
  cancelledWithoutAppNote,
  meetingAccountNotice,
  rescheduleBlockedNotice,
} from "../../appointments/meetingLinkNotices";
import type {
  AppointmentAttendee,
  ClientAppointment,
  NewClientAppointment,
  RescheduleAppointment,
} from "../../appointments/types/appointments.types";
import FreeDayPanel from "../components/FreeDayPanel";
import MobileTaskDays from "../components/MobileTaskDays";
import MobileTaskList from "../components/MobileTaskList";
import TaskDayStrip from "../components/TaskDayStrip";
import TaskSummaryCards from "../components/TaskSummaryCards";
import TaskTimeline from "../components/TaskTimeline";
import { useDayTasks } from "../hooks/useDayTasks";
import { useEverBooked } from "../hooks/useEverBooked";
import {
  atMidnight,
  dayFromParam,
  dayParam,
  isPastDay,
  isSameDay,
  nextUp,
  taskStateOf,
  taskSummary,
} from "../myTask";
import type { TaskState } from "../myTask";
import { ROUTES } from "../../../routes/paths";

/** The one thing a dialog is open about, and which dialog it is. */
type Pending =
  // Never set on a phone: there, Create is a screen rather than a dialog —
  // see `openCreate`.
  | { kind: "create" }
  | { kind: "meeting"; appointment: ClientAppointment }
  | { kind: "reschedule"; appointment: ClientAppointment }
  | { kind: "cancel"; appointment: ClientAppointment }
  | { kind: "complete"; appointment: ClientAppointment };

/**
 * The first name out of whatever the appointment called them — client or
 * enquiry, since both dialogs that read this ask the same question about an
 * hour and neither cares which kind of person it was with.
 */
const firstNameOf = (appointment: ClientAppointment): string =>
  (attendeeName(appointment) || "").trim().split(/\s+/)[0] || "your client";

/**
 * My task: one day of the practice, read as it will be lived.
 *
 * The week across the top, four figures under it, and the day itself as a
 * thread of appointments with the hour against each one. It is the only screen
 * in the app that is about a day rather than about a record — My clients is a
 * roster, Revenue is a ledger, a client's Appointment tab is one person's
 * history, and none of them answers "what am I doing this afternoon".
 *
 * The day comes off the ordinary appointments list, asked for as a window —
 * `GET /api/v1/appointments/?from=&to=`, local midnight to local midnight. The
 * booking dialogs ask the same endpoint the same way and get a different
 * answer: theirs is filtered down to what a new slot could collide with, and
 * this is the day as it stands, cancellations and completed sessions and all.
 * A day's diary has to account for the hours that did not go to plan as much
 * as the ones that did — see `fetchDayDiary` beside `fetchDayAppointments`.
 *
 * No `status` or `classification` on the request, though the endpoint offers
 * both. `status` takes one constant where this wants every one of them, and
 * `classification` is the server reading its own clock when it answered — on a
 * screen somebody leaves open all morning, "upcoming" has to keep meaning
 * upcoming as the morning passes, so the clock is read here instead.
 *
 * Everything the screen can do to an appointment is the appointments feature's
 * own: the same four dialogs the client screen opens, against the same four
 * endpoints. What is new here is the fifth — the Meeting dialog, which reads
 * one appointment back by its `public_id` — and the client picker on the
 * booking dialog, which this screen needs because a day does not imply a
 * person the way a client's record does.
 */
const MyTaskPage: React.FC = () => {
  const toast = useToast();
  const gate = useSubscriptionGate();
  // One setting for the workspace, read from the same cache the client screen
  // and the Integrate apps card read — so this costs a cache hit rather than a
  // request. Only the reschedule and cancel handlers ask it anything.
  const meetingApps = useMeetingApps();

  /**
   * Which of the two shapes this screen takes.
   *
   * Not a `md:` class. The day is a thread with a mark against every hour on a
   * desktop and three headed groups of cards on a phone — not one layout at
   * two widths, and building both would put every appointment of the day into
   * the accessibility tree twice. So only one of them is ever rendered, the
   * way the clients roster picks between its table and its cards.
   */
  const isMobile = useIsMobile();

  // The day takes the foot of the phone for itself. This screen is the one a
  // practitioner stands in for a whole morning rather than passes through, and
  // the week, the four figures and the list under them are all it should be
  // holding — a bar across the bottom offering three other places to be is
  // furniture over the thing being read, and the Create pill already floating
  // above it made two rounded bars stacked in the same corner.
  //
  // The way out is the arrow beside the heading instead, which is why this
  // screen names it rather than leaving it to the trail — see `PageHeader`.
  // Nothing happens from `md` up: no bar is drawn there to hide.
  useHiddenTabBar();

  /**
   * The day being looked at, held in `?date=` rather than in state here.
   *
   * Opens on today where the URL names no day, which is what somebody coming
   * to this screen is nearly always asking about. Held at midnight so that
   * changing the day is only ever a change of day: the value is a cache key
   * and a query param, and one carrying the hour it happened to be created at
   * would make every render a different day.
   *
   * In the URL because of the screen next door. Booking is a page of its own
   * on a phone now — see `NewTaskPage` — so pressing New task unmounts this
   * one, and a day kept in a `useState` would be gone by the time the form
   * closed: somebody who walked to Friday and cancelled would be handed back
   * this morning. The URL is the one place a day survives that round trip,
   * the back gesture and a reload, and it makes a day linkable into the
   * bargain, which `paths.ts` has been waiting for something to ask for.
   */
  const [params, setParams] = useSearchParams();
  // Read once, so the fallback cannot change identity under the memo below.
  // Midnight tonight is a problem the whole app shares and none of it solves;
  // `now` beside it is what actually ticks.
  const [startOfToday] = useState(() => atMidnight(new Date()));
  const dateParam = params.get("date");
  const day = useMemo(
    () => dayFromParam(dateParam) ?? startOfToday,
    [dateParam, startOfToday]
  );

  /**
   * Moving the screen to another day.
   *
   * `replace`, not a push: stepping along the week is changing what this
   * screen is showing rather than going somewhere, and a history entry per
   * day would make the back gesture walk the week backwards one Tuesday at a
   * time before it ever left the screen.
   */
  const setDay = useCallback(
    (picked: Date) => setParams({ date: dayParam(picked) }, { replace: true }),
    [setParams]
  );

  /**
   * One `now` for the whole screen, so the thread, the tiles and the rows
   * cannot disagree about which appointment is happening.
   *
   * It has to move on its own: a call at one o'clock has to take the ring off
   * the twelve-thirty and start asking how that one went, without anybody
   * reloading — this is a screen somebody leaves open all morning. Every
   * minute, which is the resolution appointments are booked at.
   */
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60_000);
    return () => clearInterval(timer);
  }, []);

  const {
    appointments,
    status: loadStatus,
    error,
    reload,
    schedule,
    reschedule,
    cancel,
    complete,
  } = useDayTasks(day);

  // A day that lands quickly goes straight from blank to filled: the
  // placeholders are only reached once the fetch has visibly stalled. Stepping
  // along the strip goes through it too, so moving days shimmers rather than
  // showing yesterday's rows under today's date.
  const phase = useLoadPhase(loadStatus === "loading");

  const [pending, setPending] = useState<Pending | null>(null);
  // Set while a write is with the server, so a dialog's action spins rather
  // than taking a second press and booking the appointment twice.
  const [saving, setSaving] = useState(false);

  const summary = useMemo(
    () => taskSummary(appointments, now),
    [appointments, now]
  );

  const empty = appointments.length === 0;
  const today = isSameDay(day, now);
  // Behind us. Nothing can be booked into it — the dialogs refuse a date that
  // has gone — so an empty one of these is a fact rather than an invitation.
  const past = isPastDay(day, now);

  /**
   * Whether this practice has ever booked anything, asked only when it
   * matters: the day in hand is empty, it is one that can still be filled, and
   * the server has finished saying so.
   *
   * Three conditions and each one rules out a request that would be wasted. A
   * day with rows on it has answered the question by existing. A day still
   * loading has not asked one yet. And a day that has gone says the same two
   * lines whichever way the answer falls — nothing can be booked into last
   * Monday, so there is no first-run panel to decide about.
   *
   * Which leaves the ordinary case, a practitioner opening a Tuesday with four
   * appointments on it, sending nothing at all.
   */
  const everBooked = useEverBooked({
    enabled: empty && !past && loadStatus === "ready",
  });

  /**
   * Every row's state, worked out in one pass.
   *
   * In one pass because "next" is a fact about the day rather than about a
   * row — only one appointment can hold it, and a row cannot tell on its own
   * whether it is that one. Working it out per row would be the same scan of
   * the whole list once per row, and would let two rows both decide they were
   * next on a render where the clock moved between them.
   */
  const states = useMemo(() => {
    const nextId = nextUp(appointments, now)?.id;
    return new Map<string, TaskState>(
      appointments.map((appointment) => [
        appointment.id,
        taskStateOf(appointment, now, nextId),
      ])
    );
  }, [appointments, now]);

  /**
   * Starting a booking, which is two different things at two widths.
   *
   * From `md` up it opens the dialog over the day, which is what a wide
   * screen should do: the day stays readable behind a panel one glance wide,
   * and nothing is navigated away from. On a phone it goes to a screen —
   * `NewTaskPage`, carrying the day it was opened on — because the same form
   * in a drawer was a page pretending not to be one. That page argues it, and
   * `ROUTES.newTask` says why only the phone gets it.
   *
   * One function rather than the choice made at each of the four Creates on
   * this screen — the strip's, the pill's, the welcome panel's and the free
   * day's — because four copies of it is three places for the phone and the
   * desktop to start disagreeing about what Create does.
   */
  const navigate = useNavigate();
  const openCreate = useCallback(() => {
    if (isMobile) {
      navigate(`${ROUTES.newTask}?date=${dayParam(day)}`);
      return;
    }
    setPending({ kind: "create" });
  }, [isMobile, navigate, day]);

  /** Every write fails the same way, and the dialog stays up to be tried again. */
  const report = (title: string, cause: unknown) => {
    // One failure is not described as "could not reschedule this
    // appointment": a meeting made on another account is refused whatever was
    // being done to it, and what the reader needs is the account and the tab,
    // not the act. It gets its own words and the longer stand to read them
    // in — see `meetingAccountNotice`.
    const mismatch = meetingAccountNotice(cause);
    if (mismatch) {
      toast.error(mismatch.title, mismatch.message, MEETING_NOTICE_MS);
      console.error(`${title.toLowerCase()}:`, cause);
      return;
    }

    toast.error(
      title,
      cause instanceof ApiError
        ? cause.message
        : "We could not reach the server. Check your connection and try again."
    );
    console.error(`${title.toLowerCase()}:`, cause);
  };

  const handleCreate = async (
    input: NewClientAppointment,
    attendee?: AppointmentAttendee
  ) => {
    if (saving || !attendee) return;
    setSaving(true);

    try {
      const booked = await schedule(attendee, input);
      setPending(null);
      const who = attendeeName(booked);
      toast.success(
        "Appointment scheduled",
        `${appointmentShortDate(booked.startsAt)} at ${appointmentTime(
          booked.startsAt
        )}${who ? ` with ${who}` : ""}.`
      );
      // Booked onto a day that is not the one on screen — which the dialog
      // allows, since it takes any date from today on. Following it is the
      // only answer that does not leave somebody looking at a list their new
      // appointment is plainly not in.
      const landed = new Date(booked.startsAt);
      if (!Number.isNaN(landed.getTime()) && !isSameDay(landed, day)) {
        setDay(atMidnight(landed));
      }
    } catch (cause) {
      report("Could not schedule this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleReschedule = async (input: RescheduleAppointment) => {
    if (saving || pending?.kind !== "reschedule") return;

    // An online appointment cannot be moved while nothing is making meeting
    // links — the new slot would need a new link, and there is no app left to
    // make one. The same guard the client's Appointment tab puts on its own
    // reschedule; the words are shared so the two cannot drift.
    const blocked = rescheduleBlockedNotice(
      pending.appointment.type,
      meetingApps
    );
    if (blocked) {
      toast.error(blocked.title, blocked.message, MEETING_NOTICE_MS);
      return;
    }

    setSaving(true);

    try {
      const moved = await reschedule(pending.appointment.id, input);
      setPending(null);
      toast.success(
        "Appointment rescheduled",
        `Moved to ${appointmentShortDate(moved.startsAt)} at ${appointmentTime(
          moved.startsAt
        )}.`
      );
    } catch (cause) {
      report("Could not reschedule this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (saving || pending?.kind !== "cancel") return;
    const { appointment } = pending;
    setSaving(true);

    try {
      await cancel(appointment.id, reason);
      setPending(null);
      // It goes through whether an app is connected or not — calling a session
      // off needs no link. What a disconnected workspace is owed is the line
      // about the invitation nobody withdrew.
      const note = cancelledWithoutAppNote(appointment.type, meetingApps);
      toast.success(
        "Appointment cancelled",
        `The ${appointmentTime(
          appointment.startsAt
        )} appointment with ${firstNameOf(appointment)} has been called off.${
          note ? ` ${note}` : ""
        }`,
        note ? MEETING_NOTICE_MS : undefined
      );
    } catch (cause) {
      report("Could not cancel this appointment", cause);
    } finally {
      setSaving(false);
    }
  };

  const handleComplete = async (note: string) => {
    if (saving || pending?.kind !== "complete") return;
    const { appointment } = pending;
    setSaving(true);

    try {
      await complete(appointment.id, note);
      setPending(null);
      toast.success(
        "Appointment completed",
        `The ${appointmentTime(
          appointment.startsAt
        )} session with ${firstNameOf(appointment)} is on their record.`
      );
    } catch (cause) {
      report("Could not mark this appointment completed", cause);
    } finally {
      setSaving(false);
    }
  };

  /**
   * Where the empty card is with what it needs, which is two answers rather
   * than one — the day, and then whether this practice is new.
   *
   * Folded into one phase so the card never says the wrong thing on its way to
   * the right one. The second question is only asked once the first has come
   * back empty, so there is a beat between them, and a card that drew the
   * compact lines in that beat would swap them for the first-run panel in
   * front of the one reader the panel is for. Holding the space blank until
   * both have landed is the honest version: we do not know yet.
   *
   * It goes through `useLoadPhase` for the usual reason — the probe answers
   * inside the delay nearly every time, so the shimmer is never reached and
   * the card goes straight from blank to filled.
   */
  const firstRunPhase = useLoadPhase(
    empty && !past && loadStatus === "ready" && everBooked === "unknown"
  );
  const emptyPhase = phase === "ready" ? firstRunPhase : phase;

  /**
   * Whether the empty day is the one that gets the panel: today or later, in a
   * practice that is already running, with the server having answered on both
   * counts.
   *
   * The three it is not are the three that keep exactly what they had. A day
   * that has gone cannot be booked into at all — both dialogs refuse a date
   * behind today — so a Create task button on it would be an invitation that
   * cannot be accepted. A read that failed has no day to be empty: "nothing
   * planned" over a request that never came back is a statement we are in no
   * position to make. And a practice that has never booked anything gets the
   * welcome panel, which has a job this one does not — say what the screen is
   * for before asking anybody to fill it.
   */
  const freeDay =
    empty && !past && loadStatus === "ready" && everBooked === "before";

  /**
   * What the day says when it has nothing in it **and** is not a free day the
   * panel can be offered on — see `freeDay`, which is tried first.
   *
   * Three answers, and the order they are tried in is the whole of it.
   *
   * A failure comes first: an empty day under "nothing planned" would read as
   * an answer, when nothing was ever heard back — and it keeps the compact
   * layout, because a welcome mat under an error reads as though the error
   * were the screen anybody meant to land on.
   *
   * Then a practice that has never booked anything, on today or a day ahead.
   * That is the one case the welcome layout is for and the only one it gets:
   * a screen nothing has ever been put into has a job neither the compact
   * lines nor the free day's panel can do — say what the screen is for before
   * asking anybody to fill it. It is the first thing a new workspace sees
   * here, so it is worth the room, and it is deliberately left as it was.
   *
   * Which leaves one case for the compact lines, where there used to be two:
   * a day that has gone. Nothing can be put into last Monday — both booking
   * dialogs refuse a date behind today — so an empty one of those is a fact
   * rather than an invitation, and it says what was true of it and points at
   * the days that can still be filled. The free day ahead that used to be
   * answered here is `freeDay` now — same two facts, drawn as a block with the
   * mark and the way to fill the day under them.
   */
  const emptyState = (): EmptyStateProps => {
    if (loadStatus === "failed") {
      const state = failedEmptyState(
        "We could not load this day",
        <Button variant="outline" size="sm" onClick={reload}>
          Try again
        </Button>
      );
      return error ? { ...state, description: error } : state;
    }

    if (past) {
      return {
        title: "Nothing was planned for this day",
        description: `You had no appointments on ${dayFull(
          day
        )}. Pick a day from today onwards to plan one.`,
      };
    }

    if (everBooked === "never") {
      return {
        variant: "welcome",
        illustration: <NoAppointmentsMark aria-hidden className="h-14 w-14" />,
        title: today
          ? "Nothing planned for today"
          : "Nothing planned for this day",
        description: `You have nothing on ${dayFull(
          day
        )}. Create a task to plan your day.`,
        highlights: {
          title: "Here you can",
          items: [
            "Create and plan your tasks for the day.",
            "Keep track of your upcoming and completed tasks.",
          ],
        },
        action: (
          <Button
            size="md"
            startIcon={<PlusIcon className="h-4 w-4" />}
            onClick={openCreate}
            {...gate.props("schedule an appointment")}
          >
            Create task
          </Button>
        ),
      };
    }

    // The backstop, and nothing reaches it on the ordinary paths: a free day
    // ahead in a running practice is `freeDay` and gets the panel instead, and
    // a day still waiting on the first-run probe is held behind the shimmer by
    // `emptyPhase`. What it covers is a read that settled into neither — the
    // same two lines the day behind us gets, in the tense this one is in,
    // since "had no appointments" over a Friday still to come is the wrong
    // sentence about a day that has not happened yet.
    return {
      title: today ? "Nothing planned for today" : "Nothing planned for this day",
      description: `You have nothing on ${dayFull(
        day
      )}. Create a task to plan your day.`,
    };
  };

  /**
   * The way off this week, on a phone.
   *
   * The desktop keeps it on the strip, as an eighth control after a rule. A
   * phone's strip is seven blocks filling the width edge to edge with nothing
   * to spare, so it goes up to the row that heads the screen — which is the
   * one row on a phone with room on it, and is already where that screen's
   * other actions go. It is the same `DatePicker` either way, so the panel,
   * the month-and-year drill-down and the "Go to today" row are the ones the
   * rest of the app has.
   */
  const dayPicker = (
    <DatePicker
      iconOnly
      label="Go to a date"
      // Square, and the height of the back arrow and the menu button it sits
      // between, so the three read as one row of controls.
      className="h-10 w-10"
      value={formatDisplayDate(day)}
      onValueChange={(value) => {
        const picked = parseDisplayDate(value);
        if (picked) setDay(picked);
      }}
      showToday
    />
  );

  /**
   * Create, on a phone: the app's floating pill, over the day at the bottom
   * right — see `FloatingAction`, which holds the why of the shape and where
   * it sits. The one thing particular to this screen is that the pill is the
   * only control floating over it, since the tab bar is not drawn here (see
   * `useHiddenTabBar`), so the measure it floats by is the one the bar had.
   */
  const createGate = gate.props("schedule an appointment");
  const newTask = (
    <FloatingAction
      label="New task"
      // Marked disabled to a reader and turned back by hand rather than
      // natively disabled, so the press that did not know why is answered —
      // see `lockedProps`.
      {...lockedProps(createGate, openCreate)}
    />
  );

  return (
    // Two shapes of screen rather than one narrowed. From `md` up the page
    // never scrolls and only the day inside it does; on a phone the page is
    // what scrolls, in the shell's own panel, so the week and the four figures
    // travel up out of the way as the day is read down. A phone has no height
    // to spend holding a strip and four tiles on screen over a list of six.
    <div
      className={
        isMobile
          ? "flex min-h-full flex-col p-page pt-page-top"
          : "flex h-full flex-col overflow-hidden p-page pt-page-top"
      }
    >
      {/* No actions on the heading from `md` up: Create rides the day strip
          below, which is where the design puts it and where the day it would
          be booked on is already being chosen. A phone has neither of those on
          the strip — see `dayPicker` and `newTask`. */}
      <PageHeader
        title="My task"
        // The phone's way out, since the bar that was it is not drawn here —
        // see `useHiddenTabBar` above. Named rather than left to the history,
        // so a phone opened straight onto this URL has one too.
        back={ROUTES.dashboard}
        actions={isMobile ? dayPicker : undefined}
      />

      {/* Said as soon as the request goes out, whether or not anything is drawn
          for it: this line is not seen, so it costs nothing to be early, and
          the blocks it stands for are hidden from screen readers — they are the
          shape of an answer rather than the answer. */}
      {loadStatus === "loading" && (
        <p role="status" className="sr-only">
          Loading this day’s appointments…
        </p>
      )}

      {isMobile ? (
        <>
          <div className="mt-4">
            <MobileTaskDays
              selected={day}
              onSelect={(picked) => setDay(picked)}
            />
          </div>

          <div className="mt-3">
            <TaskSummaryCards
              summary={summary}
              phase={phase}
              failed={loadStatus === "failed"}
            />
          </div>

          {/* The band the design runs edge to edge across the screen, which is
              where this page stops being about the day in the abstract and
              starts being the day itself.

              Edge to edge for the reason it is a band at all: everything above
              it is cards on a page, and a tinted strip inset to the same
              gutter would read as one more card. Cancelling the page's gutter
              and spending it again inside is what puts the heading back in
              line with the cards either side of it — see `spacing.page`.

              Sticky, so it is still on screen once the day it names has
              scrolled past its own heading. On a list of eight appointments
              that is the difference between knowing which day you are reading
              and having to scroll back up to check. */}
          <div className="sticky top-0 z-[1] -mx-page mt-4 border-y border-[#DBF2F6] bg-[#EFFBFD] px-page py-2.5">
            <h2 className="text-[16px] font-semibold text-ink">All task</h2>
          </div>

          {/* The air at the foot is for the floating Create above the tab bar:
              without it the last card of a long day ends underneath it. */}
          <div className="mt-4 pb-20">
            {emptyPhase !== "ready" && empty ? (
              // One block in the shape of a card, rather than a column of them
              // in the shape of rows nobody knows the number of yet.
              <SkeletonCard
                phase={emptyPhase}
                className="h-[220px] w-full rounded-card"
              />
            ) : empty ? (
              // In a card of its own, the way every other block on a phone's
              // screen is. No stretching to the foot of the page here — the
              // page is what scrolls, so there is no foot to stretch to, and a
              // card forced to the window's height would leave the panel
              // scrolling past the end of its own content.
              //
              // The free day's panel draws its own air top and bottom, so the
              // card holds none for it: the two together came to a block
              // floating in the middle of a card half of which was padding.
              <div
                className={`card-surface border border-line bg-white ${
                  freeDay ? "" : "py-6"
                }`}
              >
                {freeDay ? (
                  <FreeDayPanel
                    day={day}
                    gate={gate.props("schedule an appointment")}
                    onCreate={openCreate}
                  />
                ) : (
                  <EmptyState {...emptyState()} />
                )}
              </div>
            ) : (
              <MobileTaskList
                appointments={appointments}
                states={states}
                now={now}
                gate={gate.props("mark an appointment")}
                onOpen={(appointment) =>
                  setPending({ kind: "meeting", appointment })
                }
                onComplete={(appointment) =>
                  setPending({ kind: "complete", appointment })
                }
                onCancel={(appointment) =>
                  setPending({ kind: "cancel", appointment })
                }
              />
            )}
          </div>

          {newTask}
        </>
      ) : (
        <>
          <div className="mt-4 flex-shrink-0 3xl:mt-5">
            <TaskDayStrip
              selected={day}
              onSelect={(picked) => setDay(picked)}
              action={
                <Button
                  size="md"
                  startIcon={<PlusIcon className="h-4 w-4" />}
                  onClick={openCreate}
                  {...gate.props("schedule an appointment")}
                >
                  Create
                </Button>
              }
            />
          </div>

          <div className="mt-4 flex-shrink-0 3xl:mt-5">
            <TaskSummaryCards
              summary={summary}
              phase={phase}
              failed={loadStatus === "failed"}
            />
          </div>

          {emptyPhase !== "ready" && empty ? (
            // One block the height of the day, rather than a column of blocks
            // in the shape of rows nobody knows the number of yet.
            <div className="mt-4 min-h-0 flex-1 3xl:mt-5">
              <SkeletonCard
                phase={emptyPhase}
                className="h-full w-full rounded-card"
              />
            </div>
          ) : empty ? (
            <section className="mt-4 flex min-h-0 flex-1 flex-col overflow-auto card-surface border border-line bg-white 3xl:mt-5">
              {/* Both blocks take `my-auto` for the same reason, and it is the
                  one piece of layout they share.

                  Centred down the card rather than sitting at the top of it.
                  The card stretches to the foot of the page, so a block pinned
                  to its top edge leaves a third of the screen blank underneath
                  and reads as a list that failed to finish drawing.

                  `my-auto` rather than `justify-center` on the card: this card
                  scrolls, and centring a scroll container with
                  `justify-content` puts the overflow at the top out of reach —
                  on a short window the illustration would be cut off with no
                  way to scroll up to it. Equal auto margins centre the same way
                  and still let it scroll.

                  Down the card only, and `my` rather than `m` for that reason:
                  an auto margin across would stop the block stretching and pull
                  it into the middle, and it has to stay set to the left. The
                  highlights under the description hang off a rule, and a rule
                  needs a left edge to hang from — see `EmptyState`, and the
                  free day's panel is set left for a reason of its own. */}
              {freeDay ? (
                <FreeDayPanel
                  day={day}
                  gate={gate.props("schedule an appointment")}
                  onCreate={openCreate}
                  className="my-auto"
                />
              ) : (
                <EmptyState {...emptyState()} className="my-auto" />
              )}
            </section>
          ) : (
            // No card around the rows. The thread down the left is the spine of
            // this screen and a border beside it would read as a second one —
            // and the rows are already cards. Only this scrolls.
            //
            // The padding and the negative margin are one thing, not two: the
            // current row carries a soft shadow, and a scroller clips at its
            // own edge, so without a margin of air inside it the shadow is
            // sliced off down the right-hand side. Equal negative margin
            // outside puts the rows back exactly under the tiles above them, so
            // the air costs the layout nothing.
            <section
              aria-label={`Appointments on ${dayFull(day)}`}
              className="custom-scrollbar-overlay -mx-2 mt-4 min-h-0 flex-1 overflow-auto px-2 py-1 3xl:mt-5"
            >
              <TaskTimeline
                appointments={appointments}
                states={states}
                now={now}
                gate={gate.props("mark an appointment")}
                onOpen={(appointment) =>
                  setPending({ kind: "meeting", appointment })
                }
                onComplete={(appointment) =>
                  setPending({ kind: "complete", appointment })
                }
                onCancel={(appointment) =>
                  setPending({ kind: "cancel", appointment })
                }
              />
            </section>
          )}
        </>
      )}

      {/* Only ever one of the five. Each stays up while its write is with the
          server, so a booking that could not be made leaves the details on
          screen to send again rather than closing over a failure. */}
      {/* Wide screens only. A phone's Create is `ROUTES.newTask`, which is
          the same form in its page shell — see `openCreate`. */}
      {pending?.kind === "create" && (
        <ScheduleAppointmentModal
          pickClient
          title="Create meeting"
          // Opened on the day the screen is standing on, whichever of the
          // three Creates opened it — the strip's, the welcome panel's, or the
          // free day's. This screen is a day rather than a record, so a date
          // field coming up on today while Friday is lit on the strip is a
          // dialog whose first question is already answered wrongly.
          //
          // Except on a day that has gone, which is left blank rather than
          // filled with a date the dialog itself refuses: an error under the
          // first field of a dialog nobody has touched reads as the screen
          // being broken. Create is still on the strip there, and still books
          // — it just asks which day, which on a past one it has to.
          initialDate={past ? undefined : formatDisplayDate(day)}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleCreate}
        />
      )}

      {pending?.kind === "meeting" && (
        <MeetingModal
          appointment={pending.appointment}
          gate={gate.props("change an appointment")}
          onClose={() => setPending(null)}
          // The Meeting dialog hands straight over to the one that acts, so
          // the details it was read in are not sitting behind a second panel
          // saying the same thing.
          onReschedule={(appointment) =>
            setPending({ kind: "reschedule", appointment })
          }
          onCancel={(appointment) =>
            setPending({ kind: "cancel", appointment })
          }
        />
      )}

      {pending?.kind === "reschedule" && (
        <RescheduleAppointmentModal
          appointment={pending.appointment}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleReschedule}
        />
      )}

      {pending?.kind === "complete" && (
        <CompleteAppointmentModal
          appointment={pending.appointment}
          firstName={firstNameOf(pending.appointment)}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleComplete}
        />
      )}

      {pending?.kind === "cancel" && (
        <CancelAppointmentModal
          appointment={pending.appointment}
          firstName={firstNameOf(pending.appointment)}
          busy={saving}
          onClose={() => setPending(null)}
          onConfirm={handleCancel}
        />
      )}
    </div>
  );
};

export default MyTaskPage;
